import { useEffect, useRef, useState } from 'preact/hooks';

import {
  CAMERA_MESSAGE,
  classifyCameraError,
  openCamera,
  type CameraError,
  type CameraHandle,
} from '../scanner/camera';
import { createScanner, type Scanner } from '../scanner/barcode';

interface Props {
  /** Wird mit dem Rohwert des Codes aufgerufen. Doppelauslösungen fängt die Sitzung ab. */
  onCode: (raw: string) => void;
  /** Pausiert das Auslesen, solange ein Ergebnis angezeigt wird. */
  paused?: boolean;
  intervalMs?: number;
  /**
   * Meldet, welcher Erkenner läuft und wie das Bild aussieht. Nur für die
   * Fehlersuche: welcher Weg aktiv ist, sieht man dem Gerät sonst nicht an.
   */
  onDiagnose?: (d: { engine: 'native' | 'zxing'; width: number; height: number }) => void;
}

/**
 * Kamerabild mit laufender Code-Erkennung.
 *
 * Bewusst ohne Auslöser: die Duty-Kraft hält die Karte ins Bild, mehr nicht.
 * Zwischen zwei Prüfungen wird pausiert, damit derselbe Code nicht weiter
 * feuert, solange das Ergebnis noch steht.
 */
export function CameraScanner({
  onCode,
  paused = false,
  intervalMs = 250,
  onDiagnose,
}: Props) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [error, setError] = useState<CameraError | null>(null);
  const [ready, setReady] = useState(false);

  useEffect(() => {
    let camera: CameraHandle | null = null;
    let scanner: Scanner | null = null;
    let timer: ReturnType<typeof setInterval> | null = null;
    let abgebrochen = false;
    let laeuft = false;

    async function start(): Promise<void> {
      try {
        camera = await openCamera();
        if (abgebrochen) return camera.stop();

        const video = videoRef.current;
        if (video) {
          video.srcObject = camera.stream;
          // jsdom (und ältere Browser) geben hier kein Promise zurück.
          await Promise.resolve(video.play()).catch(() => undefined);
        }

        scanner = await createScanner();
        setReady(true);
        onDiagnose?.({
          engine: scanner.kind,
          width: video?.videoWidth ?? 0,
          height: video?.videoHeight ?? 0,
        });

        timer = setInterval(() => {
          void tick();
        }, intervalMs);
      } catch (err) {
        const kind =
          (err as { cameraError?: CameraError }).cameraError ?? classifyCameraError(err);
        setError(kind);
      }
    }

    async function tick(): Promise<void> {
      // Ein Durchgang mit ZXing dauert auf einem Telefon laenger als der
      // Takt. Ohne diese Sperre stapelten sich die Durchgaenge, das Geraet
      // wurde langsamer und der Code kam trotzdem nicht durch.
      if (laeuft) return;
      const video = videoRef.current;
      if (!scanner || !video || video.readyState < 2) return;
      laeuft = true;
      try {
        const raw = await scanner.scan(video);
        if (raw) onCode(raw);
      } catch {
        // Ein einzelner fehlgeschlagener Bildauszug ist belanglos.
      } finally {
        laeuft = false;
      }
    }

    void start();
    return () => {
      abgebrochen = true;
      if (timer) clearInterval(timer);
      camera?.stop();
    };
  }, []);

  // Während ein Ergebnis steht, wird nicht weiter ausgelesen.
  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;
    if (paused) video.pause();
    else void Promise.resolve(video.play()).catch(() => undefined);
  }, [paused]);

  if (error) {
    return (
      <div class="camera camera--error" role="status">
        <span>{CAMERA_MESSAGE[error]}</span>
      </div>
    );
  }

  return (
    <div class="camera">
      <video ref={videoRef} playsInline muted autoPlay aria-label="Kamerabild" />
      {!ready && <span class="camera__hint">Kamera startet …</span>}
    </div>
  );
}
